import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import authService from '../../services/authService'
import cookieUtils from '../../utils/cookieUtils'

/**
 * Get the campaigns a user belongs to (first step of login)
 */
export const getUserCampaigns = createAsyncThunk('auth/getUserCampaigns', async (username, { rejectWithValue }) => {
  try {
    const campaigns = await authService.getCampaigns(username, {
      showSnackbar: true,
      showSuccessSnackbar: false,
      errorMessage: 'Username not found'
    })
    return campaigns
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'Username not found'
    return rejectWithValue(message)
  }
}
)

/**
 * Verify password for the selected campaign and complete login
 */
export const verifyPassword = createAsyncThunk('auth/verifyPassword', async ({ userid, password, campaignid }, { getState, rejectWithValue }) => {
  try {
    const response = await authService.verifyPassword(userid, password, campaignid, {
      showSnackbar: true,
      showSuccessSnackbar: true,
      successMessage: 'Login successful!',
      errorMessage: 'Invalid password'
    })

    const user = getState().auth.user
    if (user) {
      cookieUtils.set('authUser', JSON.stringify(user), 7)
    }

    return response
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'Invalid password'
    return rejectWithValue(message)
  }
}
)

/**
 * Check authentication status
 */
export const checkAuthStatus = createAsyncThunk('auth/checkAuthStatus', async (_, { rejectWithValue }) => {
  try {
    const response = await authService.checkAuth()
    return response
  } catch (error) {
    return rejectWithValue(error.message)
  }
}
)

/**
 * Authenticate the selected campaign/user combination
 */
export const authenticateUsername = createAsyncThunk('auth/authenticateUsername', async ({ campaignid, userid }, { rejectWithValue }) => {
  try {
    const user = await authService.authenticateUsername(campaignid, userid, {
      showSnackbar: true,
      showSuccessSnackbar: false,
      errorMessage: 'Username not found'
    })
    return user
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'Username not found'
    return rejectWithValue(message)
  }
}
)

/**
 * Set password for user (first time or forgot password)
 */
export const setUserPassword = createAsyncThunk('auth/setUserPassword', async ({ userid, password, otp }, { rejectWithValue }) => {
  try {
    const response = await authService.setPassword(userid, password, otp, {
      showSnackbar: true,
      showSuccessSnackbar: true,
      successMessage: 'Password set successfully!',
      errorMessage: 'Failed to set password'
    })
    return response
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'Failed to set password'
    return rejectWithValue(message)
  }
}
)

/**
 * Forgot user password
 */
export const forgotUserPassword = createAsyncThunk('auth/forgotUserPassword', async ({ userid, password, otp }, { rejectWithValue }) => {
  try {
    const response = await authService.forgotPassword(userid, password, otp, {
      showSnackbar: true,
      showSuccessSnackbar: true,
      successMessage: 'Password reset successfully!',
      errorMessage: 'Failed to reset password'
    })
    return response
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'Failed to reset password'
    return rejectWithValue(message)
  }
})

/**
 * Change user password
 */
export const changeUserPassword = createAsyncThunk('auth/changeUserPassword', async ({ userid, currentPassword, newPassword }, { rejectWithValue }) => {
  try {
    await authService.changePassword(userid, currentPassword, newPassword, {
      showSnackbar: true,
      showSuccessSnackbar: true,
      successMessage: 'Password changed successfully!',
      errorMessage: 'Failed to change password'
    })
    return { success: true }
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'Failed to change password'
    return rejectWithValue(message)
  }
}
)

/**
 * Request OTP for user
 */
export const requestOTP = createAsyncThunk('auth/requestOTP', async ({ userid, otpTypeId }, { rejectWithValue }) => {
  try {
    await authService.requestOTP(userid, otpTypeId, {
      showSnackbar: true,
      showSuccessSnackbar: true,
      successMessage: 'OTP sent successfully!',
      errorMessage: 'Failed to send OTP'
    })
    return { success: true, otpTypeId }
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'Failed to request OTP'
    return rejectWithValue(message)
  }
}
)

/**
 * Verify OTP for user
 */
export const verifyOTP = createAsyncThunk('auth/verifyOTP', async ({ userid, otpTypeId, otp }, { rejectWithValue }) => {
  try {
    await authService.verifyOTP(userid, otpTypeId, otp, {
      showSnackbar: true,
      showSuccessSnackbar: true,
      successMessage: 'OTP verified successfully!',
      errorMessage: 'Invalid OTP code'
    })
    return { success: true, otpTypeId }
  } catch (error) {
    const message = error.response?.data?.message || error.message || 'Invalid OTP'
    return rejectWithValue(message)
  }
}
)

const initialState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  isCheckingAuth: true,
  isFirstLogin: false,
  otpRequested: false,
  otpVerified: false,
  otpTypeId: null,
  campaigns: [],
  error: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      authService.logout()
      state.user = null
      state.token = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.isFirstLogin = false
      state.error = null
      state.otpRequested = false
      state.otpVerified = false
      state.otpTypeId = null
      state.campaigns = []
    },
    clearError: (state) => {
      state.error = null
    },
    resetOTPState: (state) => {
      state.otpRequested = false
      state.otpVerified = false
      state.otpTypeId = null
    },
    clearCampaigns: (state) => {
      state.campaigns = []
    },
  },
  extraReducers: (builder) => {
    builder
      // Get user campaigns
      .addCase(getUserCampaigns.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getUserCampaigns.fulfilled, (state, action) => {
        state.isLoading = false
        state.campaigns = action.payload
        state.error = null
      })
      .addCase(getUserCampaigns.rejected, (state, action) => {
        state.isLoading = false
        state.campaigns = []
        state.error = action.payload
      })
      // Verify password
      .addCase(verifyPassword.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(verifyPassword.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = true
        state.token = action.payload.accessToken
        state.refreshToken = action.payload.refreshToken || null
        state.error = null
      })
      .addCase(verifyPassword.rejected, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = false
        state.token = null
        state.refreshToken = null
        state.error = action.payload
      })
      // Check auth status
      .addCase(checkAuthStatus.pending, (state) => {
        state.isCheckingAuth = true
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isCheckingAuth = false
        state.isAuthenticated = true
        state.user = action.payload.user
        state.token = action.payload.token
      })
      .addCase(checkAuthStatus.rejected, (state) => {
        state.isCheckingAuth = false
        state.isAuthenticated = false
        state.user = null
        state.token = null
        state.refreshToken = null
      })
      // Authenticate username
      .addCase(authenticateUsername.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(authenticateUsername.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.isFirstLogin = action.payload.isfirstlogin || false
        state.error = null
      })
      .addCase(authenticateUsername.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      // Set user password
      .addCase(setUserPassword.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(setUserPassword.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = !!action.payload.accessToken
        state.token = action.payload.accessToken
        state.isFirstLogin = false
        state.otpRequested = false
        state.otpVerified = false
        state.otpTypeId = null
        state.error = null
      })
      .addCase(setUserPassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      // Forgot user password
      .addCase(forgotUserPassword.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(forgotUserPassword.fulfilled, (state, action) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(forgotUserPassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      // Change user password
      .addCase(changeUserPassword.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      // Request OTP
      .addCase(requestOTP.pending, (state) => {
        state.isLoading = true
        state.error = null
        state.otpRequested = false
      })
      .addCase(requestOTP.fulfilled, (state, action) => {
        state.isLoading = false
        state.otpRequested = true
        state.otpTypeId = action.payload.otpTypeId
        state.error = null
      })
      .addCase(requestOTP.rejected, (state, action) => {
        state.isLoading = false
        state.otpRequested = false
        state.error = action.payload
      })
      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.isLoading = true
        state.error = null
        state.otpVerified = false
      })
      .addCase(verifyOTP.fulfilled, (state) => {
        state.isLoading = false
        state.otpVerified = true
        state.error = null
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.isLoading = false
        state.otpVerified = false
        state.error = action.payload
      })
  },
})

export const { logout, clearError, resetOTPState, clearCampaigns } = authSlice.actions;
export default authSlice.reducer;